import axios from 'axios'
import jwt from 'jsonwebtoken'
import { GetServerSideProps, GetServerSidePropsResult } from 'next'
import { ReactNode } from 'react'
import styled from 'styled-components'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailOverviewHeader from '@/components/kol/common/kol-detail-overview-header'
import KolDetailSection from '@/components/kol/common/kol-detail-section'
import AudienceAnalyses from '@/components/kol/detail/audience-analyses/audience-analyses'
import CooperationAndValueSection from '@/components/kol/detail/collaboration-and-value'
import CreativeContentAnalyses from '@/components/kol/detail/creative-content-analyses'
import EngagementAnalyses from '@/components/kol/detail/engagement-analyses'
import BasicInfo from '@/components/kol/detail/overview/basic-info'
import ContentDistribution from '@/components/kol/detail/overview/content-distribution'
import FollowerAnalysis from '@/components/kol/detail/overview/follower-analysis'
import PlatformMetricsSummary from '@/components/kol/detail/overview/platform-metrics-summary'
import Statistics from '@/components/kol/detail/overview/statistics'
import MentionedKolList from '@/components/kol/detail/similar-kols/mentioned-kol-list'
import SimilarKolList from '@/components/kol/detail/similar-kols/similar-kol-list'
import { PublicKolInfoProvider } from '@/components/kol/hooks/use-public-kol-info-context'
import useSectionTabs from '@/components/kol/hooks/use-section-tabs'
import KolDetailLayout from '@/components/kol/layout'
import { KOL_DETAIL_SECTION_TABS_HEIGHT } from '@/components/kol/navigation/section-tabs'
import { KOL_DETAIL_TOP_SUMMARY_HEIGHT } from '@/components/kol/navigation/top-summary'
import GoogleAnalyticsScript from '@/components/tracking/google-analytics-script'
import { statisticPlatformSupport } from '@/constants/kol-detail/statistic-platform-support'
import routes from '@/hooks/use-authorization/routes'
import useEffectUntilResolve from '@/hooks/use-effect-until-resolve'
import useKolDetailDuration from '@/hooks/use-kol-detail-duration'
import { useIntl } from '@/i18n/hooks/use-intl'
import { NextLayoutComponentType } from '@/types/next-layout-component-type'
import {
  publicKolInfoSchema,
  type PublicKolInfo,
} from '@/types/schema/kol/public-kol-info-schema'
import { UserSchema } from '@/types/schema/user-schema'
import { ampli, VisitKolDetailFromPublic } from '@/utils/ampli'
import {
  convertPlatformShortcode,
  PlatformShortcode,
} from '@/utils/convert-platform'
import env from '@/utils/env'
import { convertPublicKolInfoToKolSchemaModel } from '@/utils/kol/convert-public-kol-info-to-kol-schema-model'
import logger from '@/utils/logger'
import sendGoogleAnalyticsEvent from '@/utils/tracking/send-google-analytics-event'

interface KolDetailForPublicProps {
  publicKolInfo: PublicKolInfo
  publicKolInfoForOpenGraph: PublicKolInfo
}

const KolDetailForPublic: NextLayoutComponentType<KolDetailForPublicProps> = ({
  publicKolInfo,
}) => {
  const { formatMessage } = useIntl()
  const searchParams = routes.publicKolDetail.useSearchParams()

  const tabOptions = useSectionTabs(searchParams.data?.platform)

  const platform = searchParams.success
    ? convertPlatformShortcode(searchParams.data.platform)
    : undefined

  const { duration, enable3MonthDuration } = useKolDetailDuration()
  const anchorOffset =
    KOL_DETAIL_SECTION_TABS_HEIGHT + KOL_DETAIL_TOP_SUMMARY_HEIGHT
  const kolSchemaModel = convertPublicKolInfoToKolSchemaModel(publicKolInfo)

  useEffectUntilResolve(
    (resolve) => {
      if (!searchParams.success) {
        resolve()
        return
      }

      // [Staging][CE] 從套件畫面跳轉公開 KD 會無法檢視: https://www.notion.so/ikala/Staging-CE-KD-cc465659a080484e943843fccbb49495?pvs=4
      if (searchParams.data.deviceId && ampli?.client?.setDeviceId) {
        ampli.client.setDeviceId(searchParams.data.deviceId)
      }

      if (!platform) {
        return
      }

      resolve()

      if (!searchParams.data.from) {
        return
      }

      ampli.visitKolDetailFromPublic({
        kolId: publicKolInfo.id,
        kolName: publicKolInfo.name,
        kolCategory: publicKolInfo.customized_tags_names,
        platform: platform.shortcode,
        from: searchParams.data.from,
        keyword: searchParams.data.keywordFrom,
      })
    },
    [searchParams.success, platform?.shortcode],
  )

  return (
    <>
      <GoogleAnalyticsScript
        onReady={(): void =>
          sendGoogleAnalyticsEvent(
            new VisitKolDetailFromPublic({
              kolId: publicKolInfo.id,
              kolName: publicKolInfo.name,
              kolCategory: publicKolInfo.customized_tags_names,
              platform: platform?.shortcode,
              from: searchParams.success ? searchParams.data.from : undefined,
              keyword: searchParams.success
                ? searchParams.data.keywordFrom
                : undefined,
            }),
          )
        }
      />
      {tabOptions.map((tab) => {
        if (tab.key === 'overview') {
          if (!platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
                extraContent: (
                  <KolDetailOverviewHeader
                    hiddenActions
                    isKolInfoLocked
                    duration={duration}
                    enable3MonthDuration={enable3MonthDuration}
                    isFetchingKolInfo={false}
                    kolInfo={kolSchemaModel}
                    platform={platform}
                  />
                ),
              }}
              key={tab.key}
            >
              <BasicInfo
                isFetchingKolInfo={false}
                kolInfo={kolSchemaModel}
                platform={platform}
              />
              <Statistics
                isKolInfoLocked
                duration={duration}
                isFetchingKolInfo={false}
                kolInfo={kolSchemaModel}
                platform={platform}
              />
              {statisticPlatformSupport.metricPerformance.includes(
                platform.shortcode,
              ) && (
                <PlatformMetricsSummary
                  isKolInfoLocked
                  duration={duration}
                  isFetchingKolInfo={false}
                  kolInfo={kolSchemaModel}
                  platform={platform}
                />
              )}
              <FollowerAnalysis
                isKolInfoLocked
                duration={duration}
                isFetchingKolInfo={false}
                kolInfo={kolSchemaModel}
                platform={platform}
              />
              {statisticPlatformSupport.contentDistribution.includes(
                platform.shortcode,
              ) && (
                <ContentDistribution
                  isKolInfoLocked
                  duration={duration}
                  isFetchingKolInfo={false}
                  kolInfo={kolSchemaModel}
                  platform={platform}
                />
              )}
            </KolDetailSection>
          )
        }
        if (tab.key === 'similarKols') {
          if (!publicKolInfo || !platform) {
            return null
          }
          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
              }}
              key={tab.key}
            >
              <SimilarKolList kolInfo={kolSchemaModel} platform={platform} />
              {platform.shortcode === PlatformShortcode.Instagram && (
                <KolDetailDataBlock
                  colSpan={24}
                  header={{
                    title: `${platform.title} ${formatMessage({
                      id: 'kol:mentioned_influencers',
                    })}`,
                    tooltip: {
                      title: formatMessage({
                        id: 'kol:mentioned_brands_and_influencers_tooltip',
                      }),
                    },
                  }}
                  kolId={publicKolInfo.id}
                  kolMetrics='Related influencers'
                  kolName={publicKolInfo.name}
                  platform={platform.shortcode}
                >
                  <MentionedKolList
                    duration={duration}
                    kolId={publicKolInfo.id}
                    kolName={publicKolInfo.name}
                    platform={platform.shortcode}
                  />
                </KolDetailDataBlock>
              )}
            </KolDetailSection>
          )
        }

        if (tab.key === 'audienceAnalyses') {
          if (!platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
              }}
              key={tab.key}
            >
              <AudienceAnalyses
                isKolInfoLocked
                isFetchingKolInfo={false}
                kolInfo={kolSchemaModel}
                platform={platform}
              />
            </KolDetailSection>
          )
        }

        if (tab.key === 'engagementAnalyses') {
          if (!platform) {
            return null
          }

          return (
            <KolDetailSection
              header={{
                title: formatMessage({ id: tab.i18n }),
                anchor: { id: tab.key, offset: anchorOffset },
              }}
              key={tab.key}
            >
              <EngagementAnalyses
                isKolInfoLocked
                duration={duration}
                isFetchingKolInfo={false}
                kolInfo={kolSchemaModel}
                platform={platform}
              />
            </KolDetailSection>
          )
        }

        if (tab.key === 'cooperationAndValue') {
          if (!platform) {
            return null
          }

          return (
            <CooperationAndValueSection
              isKolInfoLocked
              anchor={{ id: tab.key, offset: anchorOffset }}
              duration={duration}
              isFetchingKolInfo={false}
              key={tab.key}
              kolInfo={kolSchemaModel}
              platform={platform}
            />
          )
        }

        if (tab.key === 'creativeContentAnalyses') {
          if (!platform) {
            return null
          }

          return (
            <CreativeContentAnalyses
              isKolInfoLocked
              anchor={{ id: tab.key, offset: anchorOffset }}
              duration={duration}
              isFetchingKolInfo={false}
              key={tab.key}
              kolInfo={publicKolInfo}
              platform={platform}
            />
          )
        }

        return (
          <KolDetailSection
            header={{
              title: formatMessage({ id: tab.i18n }),
              anchor: { id: tab.key, offset: anchorOffset },
            }}
            key={tab.key}
          >
            <Box>{publicKolInfo?.name}</Box>
          </KolDetailSection>
        )
      })}
    </>
  )
}

KolDetailForPublic.getLayout = (
  page,
  pageProps: KolDetailForPublicProps,
): ReactNode => {
  return (
    <PublicKolInfoProvider
      value={{
        publicKolInfo: pageProps.publicKolInfo,
        publicKolInfoForOpenGraph: pageProps.publicKolInfoForOpenGraph,
      }}
    >
      <KolDetailLayout>{page}</KolDetailLayout>
    </PublicKolInfoProvider>
  )
}

export const getServerSideProps: GetServerSideProps<
  KolDetailForPublicProps
> = async (context) => {
  const { kolId } = context.query

  const redirect = redirectToKolIfAdUser(context)
  if (redirect) {
    return redirect
  }

  try {
    const infoResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_K6RSATELLITE_API_BASE_URL}/v1/kols/${kolId}/info`,
    )

    const publicKolInfoParseResult = publicKolInfoSchema.safeParse(
      infoResponse.data,
    )

    if (!publicKolInfoParseResult.success) {
      logger.error(publicKolInfoParseResult.error, { ssr: true })
    }

    const publicKolInfo = publicKolInfoParseResult.success
      ? publicKolInfoParseResult.data
      : infoResponse.data

    return {
      props: {
        publicKolInfo: publicKolInfo,
        publicKolInfoForOpenGraph: publicKolInfo,
      },
    }
  } catch (e) {
    logger.error(e, { ssr: true })
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    }
  }
}

// UIUX - 點擊公開 KD 頁網址，如已登入者打開 KD 頁 ref: https://www.notion.so/ikala/UIUX-KD-KD-5b9d64b710bf469d851bf6ea1516098a?pvs=4
const redirectToKolIfAdUser = (
  context: Parameters<GetServerSideProps>[0],
): GetServerSidePropsResult<KolDetailForPublicProps> | undefined => {
  try {
    const { userToken } = context.req.cookies
    if (!userToken) {
      return
    }

    const user = jwt.decode(userToken) as UserSchema
    if (user?.type === 'ad') {
      return {
        redirect: {
          destination: `${env.PUBLIC_PAGE_APP_URL}${context.resolvedUrl.replace('influencer', 'kol')}`,
          permanent: false,
        },
      }
    }
  } catch (error) {
    return
  }
}

const Box = styled.div`
  grid-column: span 24 / span 24;
  border-radius: 4px;
  box-shadow: 0 0 12px 0 rgb(0, 0, 0, 0.04);
  background: ${({ theme }): string => theme.colors.white};
  height: 500px;
`

export default KolDetailForPublic
